import { useSyncExternalStore } from "react";
import type { AnchorJobStatus } from "./service";

/**
 * Jobs whose Start hand-off to n8n is still settling. `StartFailureNotifier` polls each one and
 * raises a notification if it lands in `trigger_failed`, even when the customer has left the
 * panel they clicked Start in.
 */

/** A watch that never settles (a lost worker) is dropped after this long. */
export const START_WATCH_TIMEOUT_MS = 10 * 60 * 1000;

const watched = new Map<string, number>();
const listeners = new Set<() => void>();
let snapshot: string[] = [];

function publish(): void {
  snapshot = Array.from(watched.keys());
  listeners.forEach((l) => l());
}

export function watchStartedJob(jobId: string): void {
  if (watched.has(jobId)) {
    return;
  }
  watched.set(jobId, Date.now());
  publish();
}

export function unwatchStartedJob(jobId: string): void {
  if (watched.delete(jobId)) {
    publish();
  }
}

export function watchStartedAt(jobId: string): number | undefined {
  return watched.get(jobId);
}

export function useWatchedStartJobIds(): string[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => snapshot
  );
}

export function watchedStartIdsForTests(): string[] {
  return snapshot;
}

export function clearStartWatchesForTests(): void {
  watched.clear();
  publish();
}

/** `awaiting_start` counts as still settling: right after the click the cached job can lag the
 *  server's `starting` for a moment. */
export function startWatchOutcome(
  status: AnchorJobStatus | undefined
): "watching" | "failed" | "settled" {
  if (status === "trigger_failed") {
    return "failed";
  }
  if (
    status === undefined ||
    status === "starting" ||
    status === "awaiting_start"
  ) {
    return "watching";
  }
  return "settled";
}
